import {
    DatasourceColumnId,
    ICompiledSQLTokens,
    IDatasourceSQLMap,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { DatePickerSqlTokensCompiler } from 'src/app/utils/datasource-sql-map/date-picker-sql-tokens-compiler';
import {
    multipleSelectComponentCompileSQLTokens,
    playerIdCompileSQLTokens,
    segmentContainerIdCompileSQLTokens,
} from 'src/app/utils/datasource-sql-map/utils';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import {
    IQueryContext,
    IQueryContextDatasourceColumn,
} from 'src/app/services/query-context/models/IQueryContext';
import { sqlDateTrunc } from 'src/app/utils/sql';
import { buildSplitGroupSQL, buildWhereSQL } from 'src/app/utils/query/sql';
import { TableSqlBuilderHelper } from 'src/app/components/table-dashboard-item/sql-builder-helper';
import { StringInputFilterDashboardItemComponent } from '../../common/items/filter/string-input-filter/string-input-filter.component';
import { BQSqlFunctionHelper } from 'src/app/utils/datasource-sql-map/bq-sql-function-helper';
import { MultipleSelectComponent } from 'src/app/components/multiple-select/multiple-select.component';

const DAY_NO_GROUP = `CASE
    WHEN install_day_no BETWEEN 1 AND 7 THEN CAST(install_day_no AS STRING)
    WHEN install_day_no BETWEEN 8 AND 14 THEN '8-14'
    WHEN install_day_no BETWEEN 15 AND 29 THEN '15-29'
    WHEN install_day_no >= 30 THEN '30+'
    ELSE CAST(install_day_no AS STRING)
END`;

export class BiVizDashboardUsersActivity {
    public static sqlMap: IDatasourceSQLMap = {
        dataset: 'bi_viz',
        tableId: 'dashboard_users_activity',
        columns: {
            ACTIVITY_DATE: {
                columnId: 'ACTIVITY_DATE',
                columnSQL: 'date',
                compileSQLTokens: (state) => {
                    return DatePickerSqlTokensCompiler.compileTokens(
                        'date',
                        state
                    );
                },
            },
            PLAYER_ID: {
                columnId: 'PLAYER_ID',
                columnSQL: 'player_id',
                compileSQLTokens: (state) => {
                    return playerIdCompileSQLTokens('player_id', state);
                },
            },
            ACTIVITY_APP_VERSION: {
                columnId: 'ACTIVITY_APP_VERSION',
                columnSQL: 'daily_app_version',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'daily_app_version',
                        state
                    );
                },
            },
            ACTIVITY_APP_VERSION_GROUP_MINOR: {
                columnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
                columnSQL: `${BQSqlFunctionHelper.getCallSqlStatement(
                    BQSqlFunctionHelper.VERSION_HIERARCHY,
                    ['daily_app_version']
                )}.minor`,
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        `${BQSqlFunctionHelper.getCallSqlStatement(
                            BQSqlFunctionHelper.VERSION_HIERARCHY,
                            ['daily_app_version']
                        )}.minor`,
                        state
                    );
                },
            },
            ACTIVITY_DAY_NO: {
                columnId: 'ACTIVITY_DAY_NO',
                columnSQL: 'day_no',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'day_no',
                        state
                    );
                },
            },
            INSTALL_DAY_NO: {
                columnId: 'INSTALL_DAY_NO',
                columnSQL: DAY_NO_GROUP,
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        DAY_NO_GROUP,
                        state
                    );
                },
            },
            DAILY_AD_CONSENT: {
                columnId: 'DAILY_AD_CONSENT',
                columnSQL: 'daily_ad_consent',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'daily_ad_consent',
                        state
                    );
                },
            },
            DAILY_TOTAL_ENERGY_SPENT: {
                columnId: 'DAILY_TOTAL_ENERGY_SPENT',
                columnSQL: 'energy_outflow_running_group',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'energy_outflow_running_group',
                        state
                    );
                },
            },
            ATTRIBUTION_TRAFFIC_SOURCE: {
                columnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                columnSQL: 'player_dim.attribution.unified_traffic',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_traffic',
                        state
                    );
                },
            },
            ATTRIBUTION_NETWORK_NAME: {
                columnId: 'ATTRIBUTION_NETWORK_NAME',
                columnSQL: 'player_dim.attribution.unified_network',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_network',
                        state
                    );
                },
            },
            ATTRIBUTION_AD: {
                columnId: 'ATTRIBUTION_AD',
                columnSQL: 'player_dim.attribution.unified_ad',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_ad',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_LENGTH_S: {
                columnId: 'ATTRIBUTION_AD_LENGTH_S',
                columnSQL: 'player_dim.attribution.unified_ad_dim.length_s',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_ad_dim.length_s',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_SIZE: {
                columnId: 'ATTRIBUTION_AD_SIZE',
                columnSQL: 'player_dim.attribution.unified_ad_dim.size',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_ad_dim.size',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_ASPECT_RATIO: {
                columnId: 'ATTRIBUTION_AD_ASPECT_RATIO',
                columnSQL: 'player_dim.attribution.unified_ad_dim.aspect_ratio',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_ad_dim.aspect_ratio',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_TYPE: {
                columnId: 'ATTRIBUTION_AD_TYPE',
                columnSQL: 'player_dim.attribution.unified_ad_dim.type',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_ad_dim.type',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_THEME: {
                columnId: 'ATTRIBUTION_AD_THEME',
                columnSQL: 'player_dim.attribution.unified_ad_dim.theme',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_ad_dim.theme',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_VERSION: {
                columnId: 'ATTRIBUTION_AD_VERSION',
                columnSQL: 'player_dim.attribution.unified_ad_dim.version',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_ad_dim.version',
                        state
                    );
                },
            },
            ATTRIBUTION_ADSET: {
                columnId: 'ATTRIBUTION_ADSET',
                columnSQL: 'player_dim.attribution.unified_adset',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_adset',
                        state
                    );
                },
            },
            ATTRIBUTION_CAMPAIGN_ACTIVE: {
                columnId: 'ATTRIBUTION_CAMPAIGN_ACTIVE',
                columnSQL: 'active_campaign',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.active_campaign',
                        state,
                        MultipleSelectComponent.toBooleanSQL
                    );
                },
            },
            ATTRIBUTION_CAMPAIGN_NAME: {
                columnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                columnSQL: 'player_dim.attribution.unified_campaign',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_campaign',
                        state
                    );
                },
            },
            DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL: {
                columnId: 'DEVICES_SELECTED_GA_FIRST_DEVICE_MODEL',
                columnSQL: 'player_dim.device.first_device.model',
                compileSQLTokens: (state) => {
                    return StringInputFilterDashboardItemComponent.compileSqlTokens(
                        'player_dim.device.first_device.model',
                        state
                    );
                },
            },
            ETL_PAYER: {
                columnId: 'ETL_PAYER',
                columnSQL: 'player_dim.revenue_sessions.payer_label',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.revenue_sessions.payer_label',
                        state
                    );
                },
            },
            ETL_REVENUE_BADGE: {
                columnId: 'ETL_REVENUE_BADGE',
                columnSQL: 'player_dim.revenue_sessions.revenue_badge',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.revenue_sessions.revenue_badge',
                        state
                    );
                },
            },
            FIREBASE_APP_STORE: {
                columnId: 'FIREBASE_APP_STORE',
                columnSQL: 'player_dim.revenue_sessions.first_store',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.revenue_sessions.first_store',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY_EUROPEAN_UNION: {
                columnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                columnSQL: 'player_dim.geo.first_geo.country_european_union',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.geo.first_geo.country_european_union',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY_TIER: {
                columnId: 'FIREBASE_COUNTRY_TIER',
                columnSQL: 'player_dim.geo.first_geo.country_tier_label',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.geo.first_geo.country_tier_label',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY_IS_RELEVANT_LABEL: {
                columnId: 'FIREBASE_COUNTRY_IS_RELEVANT_LABEL',
                columnSQL: 'player_dim.geo.first_geo.country_is_relevant_label',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.geo.first_geo.country_is_relevant_label',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY: {
                columnId: 'FIREBASE_COUNTRY',
                columnSQL: 'player_dim.geo.first_geo.unified_country',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.geo.first_geo.unified_country',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY_US_REGION: {
                columnId: 'FIREBASE_COUNTRY_US_REGION',
                columnSQL: 'player_dim.geo.first_geo.us_subdivision',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.geo.first_geo.us_subdivision',
                        state
                    );
                },
            },
            FIREBASE_FIRST_OPEN_DATE: {
                columnId: 'FIREBASE_FIRST_OPEN_DATE',
                columnSQL: 'player_dim.revenue_sessions.first_session_date',
                compileSQLTokens: (state) => {
                    return DatePickerSqlTokensCompiler.compileTokens(
                        'player_dim.revenue_sessions.first_session_date',
                        state
                    );
                },
            },
            FIREBASE_INSTALL_VERSION: {
                columnId: 'FIREBASE_INSTALL_VERSION',
                columnSQL: 'player_dim.revenue_sessions.install_version',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.revenue_sessions.install_version',
                        state
                    );
                },
            },
            FIREBASE_INSTALL_VERSION_GROUP_MINOR: {
                columnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                columnSQL: `${BQSqlFunctionHelper.getCallSqlStatement(
                    BQSqlFunctionHelper.VERSION_HIERARCHY,
                    ['player_dim.revenue_sessions.install_version']
                )}.minor`,
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        `${BQSqlFunctionHelper.getCallSqlStatement(
                            BQSqlFunctionHelper.VERSION_HIERARCHY,
                            ['player_dim.revenue_sessions.install_version']
                        )}.minor`,
                        state
                    );
                },
            },
            GA_FIRST_DEVICE_BRAND: {
                columnId: 'GA_FIRST_DEVICE_BRAND',
                columnSQL: 'player_dim.device.first_device.unified_brand',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.device.first_device.unified_brand',
                        state
                    );
                },
            },
            GA_FIRST_DEVICE_CATEGORY: {
                columnId: 'GA_FIRST_DEVICE_CATEGORY',
                columnSQL: 'player_dim.device.first_device.category',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.device.first_device.category',
                        state
                    );
                },
            },
            GA_FIRST_DEVICE_MODEL: {
                columnId: 'GA_FIRST_DEVICE_MODEL',
                columnSQL: 'player_dim.device.first_device.model',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.device.first_device.model',
                        state
                    );
                },
            },
            SEGMENT: {
                columnId: 'SEGMENT',
                columnSQL: '_NOT_IMPLEMENTED_',
                compileSQLTokens: segmentContainerIdCompileSQLTokens,
            },
            SEGMENT_EXCLUDE: {
                columnId: 'SEGMENT_EXCLUDE',
                columnSQL: 'segments',
                compileSQLTokens: segmentContainerIdCompileSQLTokens,
            },
        },
    };

    // compile sql statements specific for this datasource
    public static compileSQLStatements(
        supportedDatasourceColumns: IQueryContextDatasourceColumn[],
        queryContextSettings: IQueryContext['settings'],
        options: { gcpProjectId: GcpProjectId },
        customCompiledSqlTokens?: Partial<ICompiledSQLTokens>
    ) {
        const dateTrunc = queryContextSettings.dateAggregation
            ? sqlDateTrunc('date', queryContextSettings.dateAggregation)
            : `date`;

        // if app_version is present in QueryContext
        const is_in_context_app_version =
            supportedDatasourceColumns.findIndex(
                (dsCol) => dsCol.id === 'ACTIVITY_APP_VERSION'
            ) != -1;

        // if segment_container_id is present in splits or filters
        const is_in_context_segment =
            supportedDatasourceColumns.findIndex(
                (dsCol) => dsCol.id === 'SEGMENT'
            ) != -1;

        const sqlTokens: {
            id: DatasourceColumnId;
            compiledSqlTokens: Partial<ICompiledSQLTokens>;
        }[] = supportedDatasourceColumns.map((dsCol) => {
            return {
                id: dsCol.id,
                compiledSqlTokens: this.sqlMap.columns[dsCol.id]!
                    .compileSQLTokens!(dsCol.state, options),
            };
        });

        // merge tokens into single deduplicated object of type ICompiledSQLTokens
        const mergedSqlTokens: ICompiledSQLTokens = mergeSqlTokensArray(
            sqlTokens.map((sqlToken) => sqlToken.compiledSqlTokens)
        );

        // CUSTOM FILTER FOR THIS TABLE
        // sqlTokens.where.push(`validation_flag IS NULL`);

        // unnest app_version_activity if app_version is present in splits or filters
        // TODO to powinno być rozwiązane na poziomie datasource, gdzie compileSQL zwróci atrybut join, z treścią `, UNNEST...`

        const splitSelect: string = buildSplitGroupSQL(
            mergedSqlTokens.split_group
        );
        const splitGroupBy = splitSelect ? 'split_group, ' : '';

        // create splitSelect in the form: dsCol.columnSQL AS dsCol.id
        const splitSelectAsSeparateColumns =
            TableSqlBuilderHelper.getSplitSelect(sqlTokens);

        // create splitGroupBy in the form: dsCol.id
        const splitGroupByTable =
            TableSqlBuilderHelper.getSplitGroupBy(sqlTokens);
        const where: string = buildWhereSQL([
            ...mergedSqlTokens.where,
            ...(customCompiledSqlTokens?.where ?? []),
        ]);
        const join: string = mergedSqlTokens.join.join('\n');
        const windowTotal = splitGroupBy ? 'PARTITION BY split_group' : '';
        const windowRunning = splitGroupBy
            ? 'PARTITION BY split_group ORDER BY day_no'
            : 'ORDER BY day_no';

        return {
            dateTrunc,
            splitSelect,
            splitGroupBy,
            splitSelectAsSeparateColumns,
            splitGroupByTable,
            where,
            windowTotal,
            windowRunning,
            join,
        };
    }
}
